import { CSSProperties, FC, forwardRef } from 'react';
import classNames from 'classnames/bind';
import { NextgenDOMPurify } from '../utils/NextgenDOMPurify/NextgenDOMPurify';
import { TextOnlyPropsTypes, TextOnlyTheme } from './types/propType';
import Typography from './Typography/Typography';
import styles from './TextOnly.module.scss';
import { TextFormatType, TextAlignType } from './types/enum';
/**
 * @component TextOnly
 *
 * @description
 * The `TextOnly` component is designed to display a title and a description within a styled container.
 * It supports theming, text alignment, background colors, border radius, and sanitization for
 * rich-text HTML content in the description.
 *
 * @props
 *
 * `titleConfig`: An object containing title configuration:
 * - `formatType` (string): The HTML tag for the title (e.g., h1, h2). Default is `h2`.
 * - `text` (string): The text to display as the title. This is required.
 * - `color` (string, optional): The color of the title text in HEX format.
 * - `alignment` ('left' | 'center' | 'right'): Alignment of the title text. Default is `center`.
 *
 * `descriptionConfig`: An object containing description configuration:
 * - `description` (string, optional): Rich-text HTML for the description. This is sanitized before rendering.
 * - `color` (string, optional): The color of the description text in HEX format.
 * - `alignment` ('left' | 'center' | 'right', optional): Text alignment for the description. Default is `center`.
 *
 * `backgroundColor` (string, optional): The background color of the container in HEX format. Default is no background.
 *
 * `theme` (string, optional): A theme name for the component. Theme classes are applied conditionally.
 *
 * `borderRadius` (string, optional): The border radius of the container in pixels. Default is `0px`.
 * `className` (string, optional): set the custom class.
 * */

const cx = classNames.bind(styles);

const TextOnly: FC<TextOnlyPropsTypes> = forwardRef<HTMLDivElement, TextOnlyPropsTypes>(
  ({ titleConfig, descriptionConfig, backgroundColor, theme, borderRadius, className }: TextOnlyPropsTypes, ref) => {
    const { formatType, text: titleText, color: titleTextColor, alignment: titleAlignment } = titleConfig;
    const { description, color: descriptionTextColor, alignment } = descriptionConfig;
    // sanitize the description text (RTE formatted)
    const sanitizedHTML = NextgenDOMPurify(description || '', { ADD_ATTR: ['target'] });

    const containerStyle: CSSProperties = {
      ...(backgroundColor ? { backgroundColor } : {}),
      borderRadius: borderRadius ? `${borderRadius}px` : '0px',
    };

    const descriptionStyle: CSSProperties = {
      ...(descriptionTextColor ? { color: descriptionTextColor } : {}),
      textAlign: alignment,
    };

    const CClassname = cx('textonly-root', [theme], className);
    return (
      <div style={containerStyle} ref={ref} className={CClassname} data-testid="textonly-container">
        {titleText && (
          <Typography as={formatType} align={titleAlignment} theme={theme} color={titleTextColor}>
            {titleText}
          </Typography>
        )}
        {description && (
          <div
            data-testid="description-text"
            style={Object.keys(descriptionStyle).length > 0 ? descriptionStyle : undefined}
            className={`${styles['description-text']}`}
            dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
          />
        )}
      </div>
    );
  },
);
TextOnly.displayName = 'TextOnly';
export { TextFormatType, TextAlignType };
export type { TextOnlyTheme, TextOnlyPropsTypes };
export default TextOnly;
